import { sprintf } from "sprintf-js";

import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";

import {
	createValidatorInstance,
	getValidationErrors,
	getValidationMessageForField,
	hasValidationErrors
} from "@/utilities/Validator";
import ShapeValidator from "@/utilities/ShapeValidator";
import { ValidatorInterface } from "@/Models/Validators/ValidatorInterface";
import ShapeConfigurationFactory from "@/Models/ShapeConfigurations/ShapeConfigurationFactory";
import GroupedValidator from "@/Models/Validators/GroupedValidator";
import AbstractShape from "@/Models/Shapes/AbstractShape";
import { CutoutTypes } from "@/Models/CutoutTypes";
import { Offset } from "@/Models/Validators/Offset";
import { MaximumDecimalPoints } from "@/Models/Validators/MaximumDecimalPoints";

import { isModelInsideParent } from "@/functions/makerjs";
import { CutoutModelDecorator } from "@/utilities/decorators/CutoutModelDecorator";
import { ShapeModelCombineDecorator } from "@/utilities/decorators/ShapeModelDecorator";
import { CutoutNoEffectValidator } from "@/Models/Validators/CutoutNoEffectValidator";

export default {
	getters: {
		isShapeComplete: (state, getters): boolean => {
			const { shape, measurements, uploadAsModel } = getters;
			const shapeValidator = new ShapeValidator();
			return shapeValidator.isShapeComplete(shape, measurements, uploadAsModel);
		},
		getValidators: (state, getters): Function => {
			return (shape: string, instance: AbstractShape, options = {}): ValidatorInterface[] => {
				if (instance === null) {
					return [];
				}

				if (!instance.model) {
					return [];
				}

				const configuration = getters.getConfiguration(shape);
				const dimensionValidators = pick(configuration.get_validators(), [...configuration.get_dimension_keys(), ...configuration.get_modifier_keys()]);

				if(configuration.hasNewValidators) {
					return configuration.get_new_validators(instance, getters.getMinimalSize, getters.maximums, getters.dimensions);
				}

				return getters.getDimensionValidatorInstances(dimensionValidators, shape, instance, options);
			};
		},
		getGroupedValidators: (state, getters): Function => {
			return (shape: string, instance: AbstractShape, options = {}): ValidatorInterface[] => {
				const { getMinimum, getValidatorMaximums } = getters;
				const configuration = (new ShapeConfigurationFactory()).get_configuration(shape);
				const groupedValidators = pick(configuration.get_validators(), ['grouped']);

				if (isEmpty(groupedValidators)) {
					return [];
				}

				let instances = [];

				groupedValidators.grouped.forEach((group: GroupedValidator) => {
					instances = instances.concat(group.getFields().map((dimension) => {
						return createValidatorInstance(group.getValidator(), {
							dimension,
							instance,
							field: `${options['prefix'] ?? ''}${shape}-${dimension}`,
							minimum: getMinimum(dimension),
							maximums: getValidatorMaximums,
							customMessage: sprintf(group.getMessage(), getMinimum(dimension), 'cm'),
							...options,
						}) ?? false;
					}));
				});

				return instances.filter(Boolean);
			};
		},
		getValidatorMaximums: (state, getters): object => {
			return getters.maximums;
		},
		getDimensionValidators: (state, getters): ValidatorInterface[] => {
			const { shape, getShapeInstance } = getters;

			return getters.getValidators(shape, getShapeInstance);
		},
		getGroupedDimensionValidators: (state, getters): ValidatorInterface[] => {
			const { shape, getShapeInstance } = getters;

			return getters.getGroupedValidators(shape, getShapeInstance);
		},

		// TODO: remove magic.
		// This logic will be removed and replaced with `get_new_validators` in the future. In the `getValidators` getter.
		// So every shape configuration will have its own validators.
		getDimensionValidatorInstances: (state, getters): Function => {
			const { hasAllMeasurements, hasAllModifiers, getMinimum, getValidatorMaximums } = getters;

			return (dimensionValidators: Record<string, Array<unknown>>, shape: string, instance: AbstractShape, options = {}): ValidatorInterface[] => {
				if (isEmpty(shape) || !hasAllMeasurements || !hasAllModifiers || instance === null) {
					return [];
				}

				let shapeValidators = [];

				for (const [dimension, validators] of Object.entries(dimensionValidators)) {
					const validatorInstances = validators.map((validator: Function) => {

						const opts = {
							...{
								dimension,
								instance,
								field: `${options['prefix'] ?? ''}${shape}-${dimension}`,
								minimum: getMinimum(dimension),
								maximums: getValidatorMaximums,
							},
							...options
						};

						return createValidatorInstance(validator, opts) ?? false;
					}).filter(Boolean);

					shapeValidators = [...shapeValidators, ...validatorInstances];
				}

				return shapeValidators;
			};
		},
		getDimensionValidatorErrors: function (state, getters): ValidatorInterface[] {
			return getValidationErrors(getters.getDimensionValidators);
		},
		getGroupedDimensionValidatorErrors: function (state, getters): ValidatorInterface[] {
			return getValidationErrors(getters.getGroupedDimensionValidators, null, true);
		},
		hasDimensionValidatorErrors: (state, getters): boolean => getters.getDimensionValidatorErrors.length > 0,
		hasGroupedDimensionValidatorErrors: (state, getters): boolean => {
			return getters.getGroupedDimensionValidatorErrors.length > 0;
		},
		hasValidatorErrors: (state, getters): boolean => {
			const validators = [
				getters.hasDimensionValidatorErrors,
				getters.hasDrillHoleValidatorErrors,
				getters.hasCutoutValidatorErrors,
				getters.hasGroupedDimensionValidatorErrors,
				getters.hasGroupedCutoutValidatorErrors,
			];

			return validators.filter(Boolean).length > 0;
		},
		isDimensionValid: (state, getters): Function => {
			return (field): boolean => {
				if (getters.fontIsLoading) {
					return true;
				}

				return !hasValidationErrors(getters.getDimensionValidators, field);
			};
		},
		isGroupValid: (state, getters): Function => {
			return (field: string): boolean => {
				return !hasValidationErrors(getters.getGroupedDimensionValidators, field, true);
			};
		},
		getGroupedDimensionValidationMessage: (state, getters): string => {
			if (getters.hasGroupedDimensionValidatorErrors) {
				return getters.getGroupedDimensionValidators[0].getMessage();
			}

			return '';
		},
		getCutoutValidators: (state, getters): ValidatorInterface[] => {
			const { shape, cutoutPrimitives, cutout, getValidators, getExportableModel } = getters;

			if (isEmpty(shape) || state.cutoutType === CutoutTypes.none || isEmpty(cutoutPrimitives) || isEmpty(cutout)) {
				return [];
			}

			const { cutout: cutoutValidatorMessages } = window.measurementSelectorVariables.i18n.validators;

			const configuration = getters.getConfiguration(cutout.instance.shape);
			let shapeValidators = [];

			if(configuration.hasNewValidators) {
				shapeValidators = configuration.get_cutout_validators(cutout.instance, getters.cutoutDimensions);
			} else {
				shapeValidators = getValidators(cutout.instance.shape, cutout.instance, {
					minimum: 1,
					isCutout: true,
					prefix: 'cutout_',
				});
			}

			const baseShapeModelDecorator = getters.getBaseShapeModelDecorator;
			const combinedShape = (new ShapeModelCombineDecorator(new CutoutModelDecorator(baseShapeModelDecorator, getters.cutouts)).getModel());
			const baseModel = combinedShape.models.shapeModel.models.baseModel;
			const cutoutModel = combinedShape.models.cutoutModel.models.baseModel;

			shapeValidators.push(
				new CutoutNoEffectValidator(
					baseShapeModelDecorator.getModel(),
					getters.cutouts
				)
			);

			// Make sure the models cutout exists in the complete model.
			if (!cutoutModel) {
				return [];
			}

			// Only run this check if the model is completely within its parent. Otherwise, we'll just cut out the shape.
			// TODO: Exception for text as this dramatically slows down the model creation. Needs fixing
			if (isModelInsideParent(cutoutModel, baseModel ) && cutout.instance.shape !== "text" ) {
				shapeValidators.push( new Offset( {
					field: 'cutoutOffset',
					dimension: 'cutoutOffset',
					modelInstance: cutoutModel,
					parentModelInstance: baseModel,
					customMessage: sprintf(cutoutValidatorMessages.distance_from_edge, 2, 'cm'),
					isCutout: true,
				}));
			}

			shapeValidators.push(
				new MaximumDecimalPoints({
					dimension: 'cutoutOffsetLeft',
					field: 'cutoutOffsetLeft',
					value: cutout.offsets[0].in_cm(),
				})
			);

			shapeValidators.push(
				new MaximumDecimalPoints({
					dimension: 'cutoutOffsetBottom',
					field: 'cutoutOffsetBottom',
					value: cutout.offsets[1].in_cm(),
				})
			);

			return shapeValidators;
		},
		getGroupedCutoutValidators: function (state, getters): ValidatorInterface[] {
			const { shape, cutoutPrimitives, cutout, getCutoutMaximums: maximums } = getters;

			if (isEmpty(shape) || state.cutoutType === CutoutTypes.none || isEmpty(cutoutPrimitives) || isEmpty(cutout)) {
				return [];
			}

			return getters.getGroupedValidators(cutout.shape, cutout.instance, {
				maximums,
				minimum: 1,
				isCutout: true,
				prefix: 'cutout_',
			});
		},
		getCutoutValidatorErrors: function (state, getters): ValidatorInterface[] {
			return getters.getCutoutValidators.filter((validator: ValidatorInterface) => !validator.isValid());
		},
		hasCutoutValidatorErrors: function (state, getters): boolean {
			return getters.getCutoutValidatorErrors.length > 0;
		},
		getGroupedCutoutValidatorErrors: function (state, getters): ValidatorInterface[] {
			return getValidationErrors(getters.getGroupedCutoutValidators, null, true);
		},
		hasGroupedCutoutValidatorErrors: (state, getters): boolean => {
			return getters.getGroupedCutoutValidatorErrors.length > 0;
		},
		getCutoutValidationMessage(state, getters) {
			return (field: string): string => {
				return getValidationMessageForField(getters.getCutoutValidators, field);
			};
		},
		getGroupedCutoutValidationMessage: (state, getters): string => {
			if (getters.hasGroupedCutoutValidatorErrors) {
				return getters.getGroupedCutoutValidatorErrors[0].getMessage();
			}

			return '';
		},
		isCutoutValid: (state, getters) => {
			return (field: string): boolean => {
				return !hasValidationErrors(getters.getCutoutValidators, field);
			};
		},
		isCutoutGroupValid: (state, getters) => {
			return (field: string): boolean => {
				return !hasValidationErrors(getters.getGroupedCutoutValidatorErrors, field, true);
			};
		},
	}
};
